import { useState, useEffect, useCallback } from 'react'

import {
	useMutation,
	UseMutationOptions,
	UseMutationResult,
} from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { LoginRequestDTO } from '@/core/api/dtos/request/auth/login'
import { PasswordRecoveryRequestDTO } from '@/core/api/dtos/request/auth/passwordRecovery'
import { PasswordResetRequestDTO } from '@/core/api/dtos/request/auth/passwordReset'
import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { RegisterResponseDTO } from '@/core/api/dtos/response/auth/register'
import { User } from '@/core/api/entities/user'
import authService from '@/core/api/services/authService'
import userService from '@/core/api/services/userService'
import { RootState } from '@/core/store'
import {
	loginStart,
	loginSuccess,
	loginFailure,
	logout as logoutAction,
	restoreAuthState,
	setUser,
} from '@/core/store/slices/auth'

import { UserMapper } from '@core/api/mappers/userMapper'

import { LoginGoogleRequestDTO } from '../api/dtos/request/auth/loginGoogle'
import { LoginWithTokenRequestDTO } from '../api/dtos/request/auth/loginWithToken'
import { LoginResponseDTO } from '../api/dtos/response/auth/login'

export const useAuth = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [isInitialized, setIsInitialized] = useState(false)

	const { user, token, isAuthenticated, isLoading, error } = useSelector(
		(state: RootState) => state.auth
	)

	// Initialize authentication state from localStorage on component mount
	useEffect(() => {
		if (!isInitialized) {
			dispatch(restoreAuthState())
			setIsInitialized(true)
		}
	}, [dispatch, isInitialized])

	// Create a standalone refetch function that updates Redux directly
	const refetchUser = useCallback(async () => {
		if (!token) return

		try {
			const response = await userService.getCurrentUser({
				headers: {
					Authorization: `Bearer ${token}`,
				},
				params: {
					include:
						'account.royalty,account.plan,account.partnership,account.overdue,account.mrr,account.business_category.parent,account.billing_info,account.subscriptions.plan.prices,account.subscriptions.items,account.brand_identities,children_accounts',
				},
			})

			const freshUser = UserMapper.toEntity(response.data.data)

			// Update Redux state with fresh user data
			dispatch(setUser(freshUser))
		} catch (error) {
			console.error('Failed to refetch user:', error)
		}
	}, [token, dispatch])

	const sucessfulLogin = (token: string, user: User, redirectTo?: string) => {
		// Clear React Query cache on successful login to prevent stale data from previous sessions
		queryClient.clear()

		dispatch(
			loginSuccess({
				token,
				user,
			})
		)
		// Redirect after successful login
		if (redirectTo) {
			navigate(redirectTo)
		}
	}

	const fetchUserAndLogin = (token: string, redirectTo?: string) => {
		return userService
			.getCurrentUser({
				headers: {
					Authorization: `Bearer ${token}`,
				},
				params: {
					include:
						'account.royalty,account.plan,account.partnership,account.overdue,account.mrr,account.business_category.parent,account.billing_info,account.subscriptions.plan.prices,account.subscriptions.items,account.brand_identities,children_accounts',
				},
			})
			.then((response) => {
				const user = UserMapper.toEntity(response.data.data)

				sucessfulLogin(token, user, redirectTo)
			})
	}

	// Login function
	const login = useCallback(
		async (data: LoginRequestDTO, redirectTo?: string) => {
			dispatch(loginStart())

			authService
				.login(data)
				.then((data) => {
					fetchUserAndLogin(data.token, redirectTo)

					return true
				})
				.catch((err: AxiosError) => {
					const data: any = err.response?.data
					dispatch(loginFailure(data?.message || 'Login failed'))
					return false
				})

			return true
		},
		[dispatch, navigate, refetchUser]
	)

	// Login function
	const loginWithGoogle = useCallback(
		async (data: LoginGoogleRequestDTO, redirectTo?: string) => {
			dispatch(loginStart())

			authService
				.loginWithGoogle(data)
				.then((response: LoginResponseDTO) => {
					fetchUserAndLogin(response.token, redirectTo)

					return true
				})
				.catch((err: AxiosError) => {
					const data: any = err.response?.data
					dispatch(loginFailure(data?.message || 'Login failed'))
					return false
				})

			return true
		},
		[dispatch, navigate, refetchUser]
	)

	// Login function
	const loginWithToken = useCallback(
		async (data: LoginWithTokenRequestDTO, redirectTo?: string) => {
			dispatch(loginStart())

			authService
				.loginWithToken(data)
				.then((response: LoginResponseDTO) => {
					fetchUserAndLogin(response.token, redirectTo)

					return true
				})
				.catch((err: AxiosError) => {
					const data: any = err.response?.data
					dispatch(loginFailure(data?.message || 'Login failed'))
					return false
				})

			return true
		},
		[dispatch, navigate, refetchUser]
	)

	// Logout function
	const logout = useCallback(
		async (redirectTo: string = '/login') => {
			// Clear React Query cache on logout to prevent data leaking to next user
			queryClient.clear()

			// Always clear local state even if API call fails
			dispatch(logoutAction())
			navigate(redirectTo)
		},
		[dispatch, navigate, queryClient]
	)

	// Manually refresh token
	const refreshToken = useCallback(async () => {
		if (!user) return false

		try {
			const response = await authService.refreshToken()
			dispatch(
				loginSuccess({
					token: response.token,
					user: user,
				})
			)
			return true
		} catch (error: any) {
			console.error('Failed to refresh token:', error)
			dispatch(logoutAction())
			return false
		}
	}, [dispatch, user])

	const useForgotPassword = ({
		config,
		options,
	}: {
		config?: AxiosRequestConfig
		options?: UseMutationOptions<any, Error, PasswordRecoveryRequestDTO, any>
	} = {}): UseMutationResult<void, Error, any> => {
		return useMutation<any, Error, PasswordRecoveryRequestDTO, any>({
			mutationFn: (data: PasswordRecoveryRequestDTO) =>
				authService.forgotPassword(data, config),
			...options,
		})
	}

	const useResetPassword = ({
		config,
		options,
	}: {
		config?: AxiosRequestConfig
		options?: UseMutationOptions<any, Error, PasswordResetRequestDTO, any>
	} = {}): UseMutationResult<void, Error, any> => {
		return useMutation<any, Error, PasswordResetRequestDTO, any>({
			mutationFn: (data: PasswordResetRequestDTO) =>
				authService.resetPassword(data, config),
			...options,
		})
	}

	const useRegister = ({
		config,
		options,
	}: {
		config?: AxiosRequestConfig
		options?: UseMutationOptions<
			RegisterResponseDTO,
			Error,
			RegisterRequestDTO,
			any
		>
	} = {}): UseMutationResult<
		RegisterResponseDTO,
		Error,
		RegisterRequestDTO,
		any
	> => {
		return useMutation<RegisterResponseDTO, Error, RegisterRequestDTO, any>({
			mutationFn: (data: RegisterRequestDTO) =>
				authService.register(data, config).then((response) => {
					return {
						account_id: response.data.data.account_id,
						user_id: response.data.data.user_id,
						temporary_token: response.data.data.temporary_token,
						access_token: response.data.data.access_token,
					}
				}),
			...options,
		})
	}

	return {
		user,
		token,
		isAuthenticated,
		isLoading,
		error,
		login,
		loginWithGoogle,
		loginWithToken,
		sucessfulLogin,
		fetchUserAndLogin,
		logout,
		refreshToken,
		refetchUser,
		isInitialized,
		useForgotPassword,
		useResetPassword,
		useRegister,
	}
}
